import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import styles from "../../css/Dashboard.module.css";

const ViewApplications = () => {
  const { jobId } = useParams();
  const [applications, setApplications] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [sortOrder, setSortOrder] = useState("desc");
  const [interviewDates, setInterviewDates] = useState({}); // To track interview dates
  const [interviewInfo, setInterviewInfo] = useState({}); // To track interview info for each application
  const [successMessage, setSuccessMessage] = useState("");

  useEffect(() => {
    if (jobId) {
      fetchApplications(jobId);
    }
  }, [jobId, sortOrder]);

  const fetchApplications = async (jobId) => {
    try {
      setLoading(true);
      console.log('Fetching applications for job ID:', jobId);
      
      if (!jobId) {
        console.error('Job ID is undefined');
        setError('Job ID is missing. Please try refreshing the page.');
        setLoading(false);
        return;
      }
      
      const response = await axios.get(
        `https://techservicemarket-backend.onrender.com/api/recruiter/jobs/${jobId}/applications`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      const sortedApplications = response.data.sort((a, b) => {
        const dateA = new Date(a.appliedAt);
        const dateB = new Date(b.appliedAt);
        return sortOrder === "asc" ? dateA - dateB : dateB - dateA;
      });

      setApplications(sortedApplications);
    } catch (error) {
      console.error("Error fetching applications:", error);
      setError(`Failed to fetch applications: ${error.response?.data?.message || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSortByDate = () => {
    setSortOrder((prevOrder) => (prevOrder === "asc" ? "desc" : "asc"));
  };

  const handleInterviewDateChange = (applicationId, value) => {
    setInterviewDates((prevDates) => ({
      ...prevDates,
      [applicationId]: value,
    }));
  };

  const handleInterviewInfoChange = (applicationId, value) => {
    setInterviewInfo((prevInfo) => ({
      ...prevInfo,
      [applicationId]: value,
    }));
  };

  const handleScheduleInterview = async (applicationId) => {
    try {
      setLoading(true);
      
      // Check if jobId is defined
      if (!jobId) {
        setError("Job ID is missing. Please try refreshing the page.");
        setLoading(false);
        return;
      }
      
      // Get the input values
      const interviewDate = document.getElementById(`interviewDate-${applicationId}`).value;
      const interviewInfo = document.getElementById(`interviewInfo-${applicationId}`).value;
      
      if (!interviewDate) {
        setError("Please select an interview date");
        setLoading(false);
        return;
      }
      
      console.log('Scheduling interview for application:', applicationId);
      console.log('Job ID:', jobId);
      console.log('Interview date:', interviewDate);
      console.log('Interview info:', interviewInfo);
      
      // First approach - direct endpoint
      const response = await axios.put(
        `https://techservicemarket-backend.onrender.com/api/recruiter/jobs/${jobId}/applications/${applicationId}/status`,
        {
          status: "Interview Scheduled",
          interviewDate,
          interviewInformation: interviewInfo || "No additional information provided"
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      
      console.log('Interview scheduled successfully:', response.data);
      setSuccessMessage("Interview scheduled successfully! Notification sent to applicant.");
      setTimeout(() => setSuccessMessage(""), 3000);
      
      // Refresh applications list
      fetchApplications(jobId);
    } catch (error) {
      console.error("Error scheduling interview:", error);
      // Try alternative approach using the simpler endpoint if the first one fails
      try {
        console.log('Trying alternative approach for scheduling interview');
        
        const response = await axios.post(
          `https://techservicemarket-backend.onrender.com/api/recruiter/updateApplication`,
          {
            applicationId,
            status: "Interview Scheduled",
            interviewDate: document.getElementById(`interviewDate-${applicationId}`).value,
            interviewInformation: document.getElementById(`interviewInfo-${applicationId}`).value || "No additional information provided"
          },
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        
        console.log('Interview scheduled successfully (alternative):', response.data);
        setSuccessMessage("Interview scheduled successfully! Notification sent to applicant.");
        setTimeout(() => setSuccessMessage(""), 3000);
        
        // Refresh applications list
        fetchApplications(jobId);
      } catch (fallbackError) {
        console.error("Error with alternative approach:", fallbackError);
        setError(`Failed to schedule interview: ${error.response?.data?.message || error.message}`);
        setTimeout(() => setError(""), 3000);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptApplication = async (applicationId) => {
    try {
      setLoading(true);
      
      // Check if jobId is defined
      if (!jobId) {
        setError("Job ID is missing. Please try refreshing the page.");
        setLoading(false);
        return;
      }
      
      console.log('Accepting application:', applicationId);
      console.log('Job ID:', jobId);
      
      const response = await axios.put(
        `https://techservicemarket-backend.onrender.com/api/recruiter/jobs/${jobId}/applications/${applicationId}/status`,
        { status: "Accepted" },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      
      console.log('Application accepted successfully:', response.data);
      setSuccessMessage("Application accepted successfully! Notification sent to applicant.");
      setTimeout(() => setSuccessMessage(""), 3000);
      
      // Refresh applications list
      fetchApplications(jobId);
    } catch (error) {
      console.error("Error accepting application:", error);
      // Try alternative approach
      try {
        console.log('Trying alternative approach for accepting application');
        
        const response = await axios.post(
          `https://techservicemarket-backend.onrender.com/api/recruiter/updateApplication`,
          {
            applicationId,
            status: "Accepted"
          },
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        
        console.log('Application accepted successfully (alternative):', response.data);
        setSuccessMessage("Application accepted successfully! Notification sent to applicant.");
        setTimeout(() => setSuccessMessage(""), 3000);
        
        // Refresh applications list
        fetchApplications(jobId);
      } catch (fallbackError) {
        console.error("Error with alternative approach:", fallbackError);
        setError(`Failed to accept application: ${error.response?.data?.message || error.message}`);
        setTimeout(() => setError(""), 3000);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRejectApplication = async (applicationId) => {
    try {
      setLoading(true);
      
      // Check if jobId is defined
      if (!jobId) {
        setError("Job ID is missing. Please try refreshing the page.");
        setLoading(false);
        return;
      }
      
      console.log('Rejecting application:', applicationId);
      console.log('Job ID:', jobId);
      
      const response = await axios.put(
        `https://techservicemarket-backend.onrender.com/api/recruiter/jobs/${jobId}/applications/${applicationId}/status`,
        { status: "Rejected" },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      
      console.log('Application rejected successfully:', response.data);
      setSuccessMessage("Application rejected. Notification sent to applicant.");
      setTimeout(() => setSuccessMessage(""), 3000);
      
      // Refresh applications list
      fetchApplications(jobId);
    } catch (error) {
      console.error("Error rejecting application:", error);
      // Try alternative approach
      try {
        console.log('Trying alternative approach for rejecting application');
        
        const response = await axios.post(
          `https://techservicemarket-backend.onrender.com/api/recruiter/updateApplication`,
          {
            applicationId,
            status: "Rejected"
          },
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        
        console.log('Application rejected successfully (alternative):', response.data);
        setSuccessMessage("Application rejected. Notification sent to applicant.");
        setTimeout(() => setSuccessMessage(""), 3000);
        
        // Refresh applications list
        fetchApplications(jobId);
      } catch (fallbackError) {
        console.error("Error with alternative approach:", fallbackError);
        setError(`Failed to reject application: ${error.response?.data?.message || error.message}`);
        setTimeout(() => setError(""), 3000);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteApplication = async (applicationId) => {
    try {
      await axios.delete(
        `https://techservicemarket-backend.onrender.com/api/recruiter/deleteApplication`,
        {
          data: { applicationId },
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      alert("Application deleted successfully.");
      fetchApplications(jobId); // Refresh applications after deletion
    } catch (error) {
      console.error("Error deleting application:", error);
      alert("Failed to delete application.");
    }
  };
  

  return (
    <div className={styles.dashboardContainer}>
      <h1 className={styles.dashboardHeader}>Job Applications</h1>
      {(error || successMessage) && (
        <div className={error ? styles.errorAlert : styles.successAlert}>
          {error || successMessage}
        </div>
      )}
      <div className={styles.contentContainer}>
        <div className={styles.sortSection}>
          <button onClick={handleSortByDate} className={styles.sortButton}>
            Sort by Date Applied {sortOrder === "asc" ? "(old to new)" : "(new to old)"}
          </button>
        </div>
        <div className={styles.jobListSection}>
          {loading && <p>Loading applications...</p>}
          {!loading && error && <p className={styles.errorText}>{error}</p>}
          {!loading && !error && applications.length > 0 ? (
            applications.map((application) => (
              <div key={application._id} className={styles.jobCard}>
                <p>
                  <strong>Applicant:</strong> {application.applicant.firstName} {application.applicant.lastName}
                </p>
                <p>
                  <strong>Email:</strong> {application.applicant.email}
                </p>
                <p>
                  <strong>Resume:</strong>{" "}
                  <a
                    href={`data:${application.resume.contentType};base64,${application.resume.data}`}
                    download={application.resume.filename}
                  >
                    {application.resume.filename}
                  </a>
                </p>
                <p>
                  <strong>Cover Letter:</strong>{" "}
                  <div>
                    <textarea
                      rows="5"
                      cols="75"
                      className={styles.interviewInfoInput}
                      value={application.coverLetter || "Not provided"}
                    />
                  </div>
                </p>
                <p>
                  <strong>Date Applied:</strong>{" "}
                  {new Date(application.appliedAt).toLocaleString()}
                </p>
                <p>
                  <strong>Status:</strong> {application.status}
                </p>

                {/* Only show interview information and date if status is not 'Rejected' */}
                {application.status !== "Rejected" && application.status !== "Accepted" && (
                  <>
                    {application.status === "Interview Accepted" ? (
                      <>
                        <div>
                          <label htmlFor={`interviewInfo-${application.applicationId}`}>
                            Interview Information:
                          </label>
                          <br />
                        </div>

                        <div className={styles.jobActions}>
                          <div>
                            <label htmlFor={`interviewDate-${application.applicationId}`}>
                              Interview Date:
                            </label>
                            <br />
                            <input
                              type="datetime-local"
                              id={`interviewDate-${application.applicationId}`}
                              value={
                                application.interviewDate
                                  ? new Date(application.interviewDate).toISOString().slice(0, 16)
                                  : ""
                              }
                              className={styles.interviewDateInput}
                              disabled
                            />
                          </div>
                        </div>
                      </>
                    ) : (
                      <>
                        <div>
                          <label htmlFor={`interviewInfo-${application.applicationId}`}>
                            Interview Information:
                          </label>
                          <br />
                          <textarea
                            rows="5"
                            cols="75"
                            id={`interviewInfo-${application.applicationId}`}
                            className={styles.interviewInfoInput}
                            value={interviewInfo[application.applicationId] || ""}
                            onChange={(e) =>
                              handleInterviewInfoChange(application.applicationId, e.target.value)
                            }
                            placeholder="Provide interview details here..."
                            required
                          />
                        </div>

                        <div className={styles.jobActions}>
                          <div>
                            <label htmlFor={`interviewDate-${application.applicationId}`}>
                              Interview Date:
                            </label>
                            <br />
                            <input
                              type="datetime-local"
                              id={`interviewDate-${application.applicationId}`}
                              value={interviewDates[application.applicationId] || ""}
                              className={styles.interviewDateInput}
                              onChange={(e) =>
                                handleInterviewDateChange(application.applicationId, e.target.value)
                              }
                              required
                            />
                          </div>
                        </div>
                      </>
                    )}
                  </>
                )}
                
                {/* Add action buttons */}
                {application.status !== "Rejected" && application.status !== "Accepted" && (
                  <div className={styles.jobActions} style={{ marginTop: '15px' }}>
                    {application.status !== "Interview Accepted" && (
                      <button
                        onClick={() => handleScheduleInterview(application.applicationId)}
                        className={styles.inviteButton}
                        disabled={loading}
                      >
                        {loading ? 'Processing...' : 'Schedule Interview'}
                      </button>
                    )}
                    
                    <button
                      onClick={() => handleAcceptApplication(application.applicationId)}
                      className={styles.inviteButton}
                      disabled={loading}
                    >
                      {loading ? 'Processing...' : 'Accept Application'}
                    </button>
                    
                    <button
                      onClick={() => handleRejectApplication(application.applicationId)}
                      className={styles.rejectButton}
                      disabled={loading}
                    >
                      {loading ? 'Processing...' : 'Reject Application'}
                    </button>
                  </div>
                )}

                {/* If application is already accepted or rejected, show appropriate message */}
                {application.status === "Accepted" && (
                  <p className={styles.successAlert} style={{ marginTop: '10px', textAlign: 'center' }}>
                    This application has been accepted
                  </p>
                )}

                {application.status === "Rejected" && (
                  <p className={styles.errorAlert} style={{ marginTop: '10px', textAlign: 'center' }}>
                    This application has been rejected
                  </p>
                )}

                {/* Delete application button */}
                <div className={styles.jobActions} style={{ marginTop: '20px' }}>
                  <button
                    onClick={() => {
                      if (window.confirm('Are you sure you want to delete this application?')) {
                        handleDeleteApplication(application.applicationId);
                      }
                    }}
                    className={styles.rejectButton}
                    style={{ backgroundColor: '#888', marginTop: '0' }}
                  >
                    Delete Application
                  </button>
                </div>
              </div>
            ))
          ) : (
            <p>No applications found.</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default ViewApplications;